import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, styled, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { FieldValues } from 'react-hook-form';
import { Spinner } from '../../atoms';
import { ConfirmationCentered } from '../ConfirmationDialog/ConfirmationDialog';
import { EditableTableHeader } from './EditableTableHeader';
import { EditableTableRow } from './EditableTableRow';
import { EditableTableRowEdit } from './EditableTableRowEdit';
import type { EditableColumnDef, EditableTableProps, RowEditContext, SortState } from './types';

const EmptyState = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '120px',
    color: theme.palette.semantic.text.muted,
    fontSize: '1rem'
}));

const AddButtonRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1.5, 0)
}));

const TableCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
    '& .MuiTableBody-root .MuiTableCell-root': {
        borderRight: `1px solid ${theme.palette.semantic.bd.default}`
    }
}));

const ADD_ROW_KEY = '__editable_table_new_row__';

const resolveRowKey = <T,>(
    row: T,
    // eslint-disable-next-line no-unused-vars
    rowKey: keyof T | ((row: T) => string | number)
): string | number => {
    if (typeof rowKey === 'function') return rowKey(row);
    return row[rowKey] as unknown as string | number;
};

const defaultComparator = <T extends FieldValues>(
    a: T,
    b: T,
    direction: 'asc' | 'desc',
    column: EditableColumnDef<T>
): number => {
    const accessor = column.sortAccessor ?? ((row: T) => row[column.key as keyof T] as unknown as string | number);
    const av = accessor(a);
    const bv = accessor(b);
    if (av == null && bv == null) return 0;
    if (av == null) return direction === 'asc' ? -1 : 1;
    if (bv == null) return direction === 'asc' ? 1 : -1;
    if (typeof av === 'number' && typeof bv === 'number') {
        return direction === 'asc' ? av - bv : bv - av;
    }
    const sa = String(av);
    const sb = String(bv);
    return direction === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
};

export const EditableTable = <T extends FieldValues>(props: EditableTableProps<T>) => {
    const {
        rows,
        columns,
        rowKey,
        editingRowKey: controlledEditingKey,
        onEditingRowKeyChange,
        expandedRowKeys: controlledExpandedKeys,
        onExpandedRowKeysChange,
        sort: controlledSort,
        onSortChange,
        onSaveRow,
        onDeleteRow,
        canDeleteRow,
        canCancelRow,
        rowError,
        isAddingNew: controlledIsAddingNew,
        newRowDefaults,
        onAddRow,
        onCancelAdd,
        addRowLabel,
        addRowDisabled,
        alwaysShowAddButton = false,
        renderExpanded,
        actions = 'auto',
        actionsColumnLabel = '',
        actionsColumnWidth = '120px',
        expandOnEdit,
        validationMode = 'onChange',
        emptyState,
        loading = false,
        rowHeight,
        bodyCellVerticalAlign = 'middle',
        saveError = false,
        onClearSaveError,
        onDirtyStateChange,
        onEditingChange,
        cancelConfirm,
        skipSaveIfUnchanged = false,
        confirmFieldChange,
        ariaLabels
    } = props;

    const { t } = useTranslation();
    const [internalEditingKey, setInternalEditingKey] = useState<string | number | null>(null);
    const [internalExpandedKeys, setInternalExpandedKeys] = useState<Set<string | number>>(new Set());
    const [readOnlyExpandedKeys, setReadOnlyExpandedKeys] = useState<Set<string | number>>(new Set());
    const [internalSort, setInternalSort] = useState<SortState | null>(null);
    const [internalIsAddingNew, setInternalIsAddingNew] = useState(false);
    const [newRowExpanded, setNewRowExpanded] = useState(false);
    const [dirtyRowKey, setDirtyRowKey] = useState<string | number | null>(null);
    const [pendingCancel, setPendingCancel] = useState<null | 'edit' | 'add'>(null);

    const editingKey = controlledEditingKey !== undefined ? controlledEditingKey : internalEditingKey;
    const expandedKeys = controlledExpandedKeys ?? internalExpandedKeys;
    const sort = controlledSort !== undefined ? controlledSort : internalSort;
    const isAddingNew = controlledIsAddingNew !== undefined ? controlledIsAddingNew : internalIsAddingNew;

    useEffect(() => {
        onDirtyStateChange?.(dirtyRowKey !== null);
    }, [dirtyRowKey, onDirtyStateChange]);

    useEffect(() => {
        onEditingChange?.(editingKey !== null || isAddingNew);
    }, [editingKey, isAddingNew, onEditingChange]);

    const setEditingKey = useCallback(
        (key: string | number | null) => {
            if (onEditingRowKeyChange) onEditingRowKeyChange(key);
            else setInternalEditingKey(key);
        },
        [onEditingRowKeyChange]
    );

    const setExpandedKeys = useCallback(
        (next: Set<string | number>) => {
            if (onExpandedRowKeysChange) onExpandedRowKeysChange(next);
            else setInternalExpandedKeys(next);
        },
        [onExpandedRowKeysChange]
    );

    const setSort = useCallback(
        (next: SortState | null) => {
            if (onSortChange) onSortChange(next);
            else setInternalSort(next);
        },
        [onSortChange]
    );

    const showActions = actions !== 'none';
    const canDelete = useCallback((row: T) => (canDeleteRow ? canDeleteRow(row) : true), [canDeleteRow]);
    const canCancel = useCallback((row: T) => (canCancelRow ? canCancelRow(row) : true), [canCancelRow]);
    const editLocked = editingKey !== null || isAddingNew || dirtyRowKey !== null;
    const canAddRow = !!(onAddRow || addRowLabel || newRowDefaults);
    const isAddingControlled = controlledIsAddingNew !== undefined;

    useEffect(() => {
        if (editingKey === null && !isAddingNew && dirtyRowKey !== null) {
            setDirtyRowKey(null);
        }
    }, [editingKey, isAddingNew, dirtyRowKey]);

    const [autoAddReady, setAutoAddReady] = useState(false);
    useEffect(() => {
        setAutoAddReady(true);
    }, []);

    useEffect(() => {
        if (!autoAddReady) return;
        if (isAddingControlled) return;
        if (loading) return;
        if (!canAddRow) return;
        if (rows.length === 0 && !internalIsAddingNew) {
            setInternalIsAddingNew(true);
        }
    }, [autoAddReady, rows.length, loading, canAddRow, isAddingControlled, internalIsAddingNew]);

    const sortedRows = useMemo(() => {
        if (!sort) return rows;
        const column = columns.find((c) => c.key === sort.key);
        if (!column) return rows;
        const comparator = column.sortComparator
            ? (a: T, b: T) => column.sortComparator!(a, b, sort.direction)
            : (a: T, b: T) => defaultComparator(a, b, sort.direction, column);
        return [...rows].sort(comparator);
    }, [rows, sort, columns]);

    const handleToggleExpand = useCallback(
        (key: string | number, isReadOnlyToggle = false) => {
            const isOpening = !expandedKeys.has(key);
            const next = new Set(expandedKeys);
            if (isOpening) next.add(key);
            else next.delete(key);
            setExpandedKeys(next);
            setReadOnlyExpandedKeys((prev) => {
                if (isOpening) {
                    if (!isReadOnlyToggle || prev.has(key)) return prev;
                    const nextRO = new Set(prev);
                    nextRO.add(key);
                    return nextRO;
                }
                if (!prev.has(key)) return prev;
                const nextRO = new Set(prev);
                nextRO.delete(key);
                return nextRO;
            });
        },
        [expandedKeys, setExpandedKeys]
    );

    const closeExpanded = useCallback(
        (key: string | number) => {
            if (!expandedKeys.has(key)) return;
            const next = new Set(expandedKeys);
            next.delete(key);
            setExpandedKeys(next);
            setReadOnlyExpandedKeys((prev) => {
                if (!prev.has(key)) return prev;
                const nextRO = new Set(prev);
                nextRO.delete(key);
                return nextRO;
            });
        },
        [expandedKeys, setExpandedKeys]
    );

    const openExpanded = useCallback(
        (key: string | number) => {
            if (expandedKeys.has(key)) return;
            const next = new Set(expandedKeys);
            next.add(key);
            setExpandedKeys(next);
        },
        [expandedKeys, setExpandedKeys]
    );

    const handleEdit = useCallback(
        (key: string | number) => {
            setEditingKey(key);
            if (expandOnEdit) {
                const row = rows.find((r) => resolveRowKey(r, rowKey) === key);
                if (row && expandOnEdit(row)) openExpanded(key);
            }
        },
        [setEditingKey, openExpanded, expandOnEdit, rows, rowKey]
    );

    const handleCancelEdit = useCallback(() => {
        if (onCancelAdd) onCancelAdd();
        if (editingKey !== null) closeExpanded(editingKey);
        setEditingKey(null);
        setDirtyRowKey(null);
    }, [onCancelAdd, setEditingKey, closeExpanded, editingKey]);

    const handleSaveEdit = useCallback(
        async (values: T) => {
            // For an existing row with no unsaved changes, skip API call and exit edit mode.
            const isUnchanged = editingKey !== null && dirtyRowKey !== editingKey;
            if (!(skipSaveIfUnchanged && isUnchanged)) {
                await onSaveRow(values);
            }
            if (editingKey !== null) closeExpanded(editingKey);
            setEditingKey(null);
            setDirtyRowKey(null);
        },
        [onSaveRow, setEditingKey, closeExpanded, editingKey, dirtyRowKey, skipSaveIfUnchanged]
    );

    const handleAddClick = useCallback(() => {
        if (onAddRow) onAddRow();
        else setInternalIsAddingNew(true);
    }, [onAddRow]);

    const handleCancelAdd = useCallback(() => {
        if (onCancelAdd) onCancelAdd();
        setInternalIsAddingNew(false);
        setNewRowExpanded(false);
        setDirtyRowKey(null);
    }, [onCancelAdd]);

    const handleSaveAdd = useCallback(
        async (values: T) => {
            await onSaveRow(values);
            setInternalIsAddingNew(false);
            setNewRowExpanded(false);
            setDirtyRowKey(null);
        },
        [onSaveRow]
    );

    // Cancel entry points that gate on unsaved changes when a confirmation is configured.
    const requestCancelEdit = useCallback(() => {
        if (cancelConfirm && editingKey !== null && dirtyRowKey === editingKey) {
            setPendingCancel('edit');
        } else {
            handleCancelEdit();
        }
    }, [cancelConfirm, editingKey, dirtyRowKey, handleCancelEdit]);

    const requestCancelAdd = useCallback(() => {
        if (cancelConfirm && dirtyRowKey === ADD_ROW_KEY) {
            setPendingCancel('add');
        } else {
            handleCancelAdd();
        }
    }, [cancelConfirm, dirtyRowKey, handleCancelAdd]);

    const handleConfirmDiscard = useCallback(() => {
        const which = pendingCancel;
        setPendingCancel(null);
        if (which === 'edit') handleCancelEdit();
        else if (which === 'add') handleCancelAdd();
    }, [pendingCancel, handleCancelEdit, handleCancelAdd]);

    const colSpan = columns.length + (showActions ? 1 : 0);
    const addRowSeed = (newRowDefaults ?? ({} as Partial<T>)) as T;
    const rowsExpandable = !!renderExpanded;

    return (
        <>
            <TableCard>
                <TableContainer>
                    <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        <EditableTableHeader
                            columns={columns}
                            sort={sort ?? null}
                            onSortChange={setSort}
                            sortDisabled={editLocked}
                            rowHeight={rowHeight}
                            actionsColumnLabel={actionsColumnLabel}
                            actionsColumnWidth={actionsColumnWidth}
                            showActions={showActions}
                        />
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={colSpan} align="center" sx={{ padding: '24px 0' }}>
                                        <div style={{ position: 'relative', minHeight: '120px' }}>
                                            <Spinner enabled={true} position="absolute" />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}

                            {!loading && sortedRows.length === 0 && !isAddingNew && (
                                <TableRow>
                                    <TableCell colSpan={colSpan} align="center" sx={{ padding: '16px 0' }}>
                                        <EmptyState>{emptyState ?? t('TEXT_NO_DATA_FOUND')}</EmptyState>
                                    </TableCell>
                                </TableRow>
                            )}

                            {!loading &&
                                sortedRows.map((row, index) => {
                                    const key = resolveRowKey(row, rowKey);
                                    const isEditing = editingKey === key;
                                    const isExpanded = expandedKeys.has(key);
                                    const isInteractive = isEditing || isExpanded;
                                    const lockedByOther = editLocked && !isEditing;
                                    const isLastRow = index === sortedRows.length - 1 && !isAddingNew;

                                    const expansionRenderer =
                                        renderExpanded && isExpanded
                                            ? (formCtx: RowEditContext<T>) =>
                                                  renderExpanded(row, {
                                                      close: () => {
                                                          closeExpanded(key);
                                                          if (isEditing) setEditingKey(null);
                                                      },
                                                      form: formCtx,
                                                      isEditing,
                                                      startEditing: () => {
                                                          setReadOnlyExpandedKeys((prev) => {
                                                              if (!prev.has(key)) return prev;
                                                              const nextRO = new Set(prev);
                                                              nextRO.delete(key);
                                                              return nextRO;
                                                          });
                                                          handleEdit(key);
                                                      },
                                                      isLocked: readOnlyExpandedKeys.has(key) || lockedByOther
                                                  })
                                            : undefined;

                                    return (
                                        <React.Fragment key={key}>
                                            {isInteractive ? (
                                                <EditableTableRowEdit
                                                    row={row}
                                                    columns={columns}
                                                    mode={isEditing ? 'edit' : 'view'}
                                                    onSave={handleSaveEdit}
                                                    onCancel={requestCancelEdit}
                                                    showCancel={canCancel(row)}
                                                    onStartEdit={
                                                        !isEditing && actions === 'auto' && !editLocked
                                                            ? () => {
                                                                  //remove the key from readOnlyExpandedKeys to allow editing
                                                                  setReadOnlyExpandedKeys((prev) => {
                                                                      if (!prev.has(key)) return prev;
                                                                      const nextRO = new Set(prev);
                                                                      nextRO.delete(key);
                                                                      return nextRO;
                                                                  });
                                                                  handleEdit(key);
                                                              }
                                                            : undefined
                                                    }
                                                    onDelete={
                                                        !isEditing && actions === 'auto' && onDeleteRow && !editLocked
                                                            ? () => onDeleteRow(row)
                                                            : undefined
                                                    }
                                                    onToggleExpand={
                                                        rowsExpandable ? () => handleToggleExpand(key) : undefined
                                                    }
                                                    onDirtyChange={(dirty) =>
                                                        setDirtyRowKey(
                                                            dirty ? key : (prev) => (prev === key ? null : prev)
                                                        )
                                                    }
                                                    showActions={showActions}
                                                    expandable={rowsExpandable}
                                                    actionsColumnWidth={actionsColumnWidth}
                                                    rowHeight={rowHeight}
                                                    validationMode={validationMode}
                                                    ariaLabels={ariaLabels}
                                                    renderExpansion={expansionRenderer}
                                                    expansionColSpan={colSpan}
                                                    isLast={isLastRow}
                                                    saveError={saveError && isEditing}
                                                    onClearSaveError={onClearSaveError}
                                                    confirmFieldChange={confirmFieldChange}
                                                />
                                            ) : (
                                                <EditableTableRow
                                                    row={row}
                                                    rowKey={key}
                                                    columns={columns}
                                                    isExpanded={false}
                                                    expandable={rowsExpandable}
                                                    onToggleExpand={(k, isReadOnlyToggle) =>
                                                        handleToggleExpand(k, isReadOnlyToggle)
                                                    }
                                                    onEdit={actions === 'auto' ? () => handleEdit(key) : undefined}
                                                    onDelete={
                                                        actions === 'auto' && onDeleteRow && canDelete(row)
                                                            ? () => onDeleteRow(row)
                                                            : undefined
                                                    }
                                                    showActions={showActions}
                                                    actionsColumnWidth={actionsColumnWidth}
                                                    rowHeight={rowHeight}
                                                    bodyCellVerticalAlign={bodyCellVerticalAlign}
                                                    editDisabled={editLocked}
                                                    ariaLabels={ariaLabels}
                                                    lockedByOther={lockedByOther}
                                                    hasError={rowError ? rowError(row) : false}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })}

                            {!loading && isAddingNew && (
                                <EditableTableRowEdit
                                    row={addRowSeed}
                                    columns={columns}
                                    onSave={handleSaveAdd}
                                    onCancel={requestCancelAdd}
                                    showCancel={canCancel(addRowSeed)}
                                    onToggleExpand={rowsExpandable ? () => setNewRowExpanded((v) => !v) : undefined}
                                    onDirtyChange={(dirty) =>
                                        setDirtyRowKey(
                                            dirty ? ADD_ROW_KEY : (prev) => (prev === ADD_ROW_KEY ? null : prev)
                                        )
                                    }
                                    showActions={showActions}
                                    expandable={rowsExpandable}
                                    actionsColumnWidth={actionsColumnWidth}
                                    rowHeight={rowHeight}
                                    validationMode={validationMode}
                                    ariaLabels={ariaLabels}
                                    renderExpansion={
                                        renderExpanded && newRowExpanded
                                            ? (formCtx) =>
                                                  renderExpanded(addRowSeed, {
                                                      close: handleCancelAdd,
                                                      form: formCtx,
                                                      isEditing: true,
                                                      startEditing: () => undefined,
                                                      isLocked: false
                                                  })
                                            : undefined
                                    }
                                    expansionColSpan={colSpan}
                                    isLast
                                    saveError={saveError}
                                    onClearSaveError={onClearSaveError}
                                    confirmFieldChange={confirmFieldChange}
                                />
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </TableCard>
            {(onAddRow || addRowLabel) && (alwaysShowAddButton || !isAddingNew) && (
                <AddButtonRow>
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={handleAddClick}
                        disabled={editLocked || addRowDisabled}
                        sx={{ borderRadius: '24px', textTransform: 'none' }}
                    >
                        {addRowLabel ?? t('TEXT_ADD')}
                    </Button>
                </AddButtonRow>
            )}
            {pendingCancel && cancelConfirm && (
                <ConfirmationCentered
                    open
                    title={cancelConfirm.title}
                    content={cancelConfirm.content}
                    confirmButtonLabel={cancelConfirm.stayLabel}
                    cancelButtonLabel={cancelConfirm.leaveLabel}
                    onConfirmation={() => setPendingCancel(null)}
                    onCancellation={handleConfirmDiscard}
                    onCloseIconClick={() => setPendingCancel(null)}
                />
            )}
        </>
    );
};
